import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { View } from 'react-native';
import { LinkedAccount } from '@/types/residents/home';
import { DesktopConnectionsTabsHeader } from './DesktopConnectionsTabsHeader';
import { DesktopLinkedAccountsList } from './DesktopLinkedAccountsList';
import { InvitationsTab } from '@/components/residents/invitations/InvitationsTab';
import { DesktopFAQScreen } from '@/screens/residents/faq/DesktopFAQScreen';
import { styles } from './DesktopLinkedAccountsStyles';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchInvitations } from '@/store/slices/residents/invitationsSlice';
import { UserType } from '@/types/residents/user';

interface DesktopLinkedAccountsViewProps {
  linkedAccounts: LinkedAccount[];
  isLoading: boolean;
  onRefresh: () => void;
  onRemove: (account: LinkedAccount) => void;
  onSendInvite: () => void;
}

export const DesktopLinkedAccountsView: React.FC<DesktopLinkedAccountsViewProps> = ({
  linkedAccounts,
  isLoading,
  onRefresh,
  onRemove,
  onSendInvite,
}) => {
  const dispatch = useAppDispatch();
  const { invitations, isLoading: isLoadingInvites } = useAppSelector((s) => s.invitations);
  const { user } = useAppSelector((s) => s.auth);
  const [activeTab, setActiveTab] = useState<'accounts' | 'invitations' | 'how'>('accounts');
  const hasAccounts = linkedAccounts.length > 0;
  const accountsCount = linkedAccounts.length;
  const invitationsCount = isLoadingInvites ? undefined : (invitations?.length || 0);

  const firstTabTitle = useMemo(() => {
    return user?.type === UserType.RESIDENT ? 'Linked Accounts' : 'Recipients';
  }, [user]);

  useEffect(() => {
    dispatch(fetchInvitations());
  }, [dispatch]);

  return (
    <View style={styles.container}>
      <DesktopConnectionsTabsHeader
        firstTabTitle={firstTabTitle}
        accountsCount={accountsCount}
        invitationsCount={invitationsCount}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {activeTab === 'accounts' && (
        <DesktopLinkedAccountsList
          isLoading={isLoading}
          hasAccounts={hasAccounts}
          accounts={linkedAccounts}
          onRemove={onRemove}
        />
      )}
      {activeTab === 'invitations' && (
        <View style={styles.invitationsContainer}>
          <InvitationsTab onSendInvite={onSendInvite} />
        </View>
      )}
      {activeTab === 'how' && (
        <View style={styles.faqContainer}>
          <DesktopFAQScreen />
        </View>
      )}
    </View>
  );
};

export default DesktopLinkedAccountsView;


